import { z } from "zod";
import {
  MAX_SITE_ACTION_FILES,
  SITE_EDIT_INSTRUCTIONS_MAX_LENGTH,
  createDraftInputSchema,
  sitywebDraftRequesterSchema,
  uuidSchema,
} from "./domain";

// Contract of the Sityweb endpoints used by Polaris Leads to link leads to
// their sites and to request site edits (ADR 0027).

const siteSlugSchema = z.string().regex(/^[a-z0-9][a-z0-9-]{1,61}[a-z0-9]$/);

/** Slugs of the Sityweb preview links (`{slug}.preview.sityweb.it`) in a text. */
export function previewSlugsIn(text: string): string[] {
  const slugs = new Set<string>();
  for (const match of text.matchAll(
    /\b([a-z0-9][a-z0-9-]{1,61}[a-z0-9])\.preview\.sityweb\.it(?![a-z0-9-]|\.[a-z0-9])/gi,
  )) {
    if (match[1]) slugs.add(match[1].toLowerCase());
  }
  return [...slugs];
}

/** A message that left for the lead, from Polaris or from the phone. */
export const SENT_MESSAGE_STATUSES = ["sent", "delivered", "read"] as const;

/** Slugs of the preview links in the messages the lead received (ADR 0057). */
export function sentPreviewSlugs(bodies: Iterable<string | null | undefined>): Set<string> {
  const slugs = new Set<string>();
  for (const body of bodies) {
    if (body) for (const slug of previewSlugsIn(body)) slugs.add(slug);
  }
  return slugs;
}

/** The lead received the link of this preview, in whatever form it was written. */
export function isPreviewLinkSent(previewUrl: string, sentSlugs: ReadonlySet<string>): boolean {
  return previewSlugsIn(previewUrl).some((slug) => sentSlugs.has(slug));
}

export const sitywebSiteResolveRequestSchema = z
  .object({ slugs: z.array(siteSlugSchema).min(1).max(100) })
  .strict();

export const sitywebSiteSchema = z
  .object({
    id: z.string().min(1).max(100),
    slug: siteSlugSchema,
    name: z.string().max(300),
    status: z.string().max(40),
    previewUrl: z.string().url(),
    customDomain: z.string().max(253).nullable(),
    /** Published and where, as in the site status below; optional so either side can ship first. */
    online: z.boolean().optional(),
    liveUrl: z.string().max(2_048).nullable().optional(),
  })
  .strict();
export type SitywebSite = z.infer<typeof sitywebSiteSchema>;

/**
 * How a site came out, from the generator's design summary: Polaris tells the
 * lead about it when it sends the draft. Empty when the site has none.
 */
export const sitywebSiteDesignSchema = z
  .object({
    aestheticDirection: z.string().max(500).nullable(),
    signatureElements: z.array(z.string().max(500)).max(5),
  })
  .strict();
export type SitywebSiteDesign = z.infer<typeof sitywebSiteDesignSchema>;

export const sitywebSiteResolveResponseSchema = z
  .object({ sites: z.array(sitywebSiteSchema) })
  .strict();

/** Subscription states of a paying site, also while a payment is being recovered. */
export const SITYWEB_CUSTOMER_SUBSCRIPTION_STATUSES = [
  "active",
  "trialing",
  "past_due",
  "unpaid",
] as const;

export const sitywebSiteStatusRequestSchema = z
  .object({ siteIds: z.array(z.string().min(1).max(100)).min(1).max(200) })
  .strict();

export const sitywebSiteStatusSchema = z
  .object({
    id: z.string().min(1).max(100),
    slug: siteSlugSchema,
    status: z.string().max(40),
    /** The site has a paying subscription: its contact is a customer. */
    customer: z.boolean(),
    plan: z.string().max(40).nullable(),
    subscriptionStatus: z.string().max(40).nullable(),
    /**
     * Name shown in Sityweb, which the admin can change: Polaris keeps the
     * linked site's name in step. Optional so either side can ship first.
     */
    name: z.string().max(300).optional(),
    /**
     * The site is published: «Sito» opens `liveUrl`, its public address (the
     * customer's domain or Sityweb's), and the preview stays one click away.
     * Optional so either side can ship first; Polaris keeps only https URLs.
     */
    online: z.boolean().optional(),
    liveUrl: z.string().max(2_048).nullable().optional(),
  })
  .strict();
export type SitywebSiteStatus = z.infer<typeof sitywebSiteStatusSchema>;

export const sitywebSiteStatusResponseSchema = z
  .object({ sites: z.array(sitywebSiteStatusSchema) })
  .strict();

export const sitywebAssetSchema = z
  .object({
    id: z.string().min(1).max(100),
    status: z.enum(["processing", "ready", "failed"]),
    errorMessage: z.string().max(2_000).nullable(),
  })
  .strict();
export type SitywebAsset = z.infer<typeof sitywebAssetSchema>;

export const sitywebEditRequestSchema = z
  .object({
    workspaceId: uuidSchema,
    leadId: uuidSchema,
    instructions: z.string().trim().min(1).max(SITE_EDIT_INSTRUCTIONS_MAX_LENGTH),
    /** Explicit operator approval: update production only if the site is already online. */
    publish: z.boolean().optional(),
    assetIds: z.array(z.string().uuid()).max(MAX_SITE_ACTION_FILES),
  })
  .strict();
export type SitywebEditRequest = z.infer<typeof sitywebEditRequestSchema>;

/**
 * Where an edit request is: `ready` once the preview (or requested production) shows it, `no_changes`
 * when the editor changed nothing, `blocked` by the review, `failed` edit or
 * build. `changes` lists what the editor changed, one line each.
 */
export const SITYWEB_EDIT_REQUEST_STATUSES = [
  "pending",
  "ready",
  "no_changes",
  "blocked",
  "failed",
] as const;
export const sitywebEditRequestStatusSchema = z
  .object({
    status: z.enum(SITYWEB_EDIT_REQUEST_STATUSES),
    changes: z.string().max(2_000).nullable(),
    publication: z.enum(["draft", "pending", "published", "failed"]).optional(),
  })
  .strict();
export type SitywebEditRequestStatus = z.infer<typeof sitywebEditRequestStatusSchema>;

/** Purpose of an uploaded file for the site generator: gallery photo or logo (ADR 0035). */
export const sitywebAssetPurposeSchema = z.enum(["gallery", "logo"]);
export type SitywebAssetPurpose = z.infer<typeof sitywebAssetPurposeSchema>;

// Starts the generation of a draft created from Polaris (ADR 0035); the answer
// is the site, also when it had already started.
export const sitywebStartCreationRequestSchema = z
  .object({
    workspaceId: uuidSchema,
    leadId: uuidSchema,
    /** Where the business works, for the search of its real data (Google Places). */
    businessLocation: z.string().trim().min(1).max(200).nullable(),
  })
  .strict();
export type SitywebStartCreationRequest = z.infer<typeof sitywebStartCreationRequestSchema>;

/**
 * Drafts of a lead archived in Polaris (Polaris ADR 0065): Sityweb deletes
 * them as the backoffice's "Elimina" does, and keeps any site that is not a
 * draft, saying why: published, on its own domain or with a subscription.
 */
export const SITYWEB_DRAFT_KEEP_REASONS = [
  "not_found",
  "published",
  "custom_domain",
  "subscription",
] as const;
export const sitywebDeleteDraftsRequestSchema = z
  .object({
    workspaceId: uuidSchema,
    leadId: uuidSchema,
    siteIds: z.array(z.string().min(1).max(100)).min(1).max(20),
  })
  .strict();
export type SitywebDeleteDraftsRequest = z.infer<typeof sitywebDeleteDraftsRequestSchema>;
export const sitywebDraftDeletionSchema = z
  .object({
    id: z.string().min(1).max(100),
    /** `deleting`: queued; `archived`: already deleted or archived; `kept`: not a draft. */
    outcome: z.enum(["deleting", "archived", "kept"]),
    reason: z.enum(SITYWEB_DRAFT_KEEP_REASONS).nullable(),
  })
  .strict();
export type SitywebDraftDeletion = z.infer<typeof sitywebDraftDeletionSchema>;
export const sitywebDeleteDraftsResponseSchema = z
  .object({ sites: z.array(sitywebDraftDeletionSchema) })
  .strict();

// Mirror of Sityweb's media upload allowlist (packages/shared/src/media-upload.ts):
// other files of the lead (e.g. WhatsApp voice notes) are not sent.
const SITYWEB_UPLOAD_LIMITS: Record<string, number> = {
  "image/jpeg": 15 * 1024 * 1024,
  "image/jpg": 15 * 1024 * 1024,
  "image/png": 15 * 1024 * 1024,
  "image/webp": 15 * 1024 * 1024,
  "image/avif": 15 * 1024 * 1024,
  "image/heic": 15 * 1024 * 1024,
  "image/heif": 15 * 1024 * 1024,
  "image/gif": 15 * 1024 * 1024,
  "application/pdf": 40 * 1024 * 1024,
  "video/mp4": 15 * 1024 * 1024,
  "audio/mpeg": 15 * 1024 * 1024,
};

/** Whether Sityweb accepts this file as a site asset. */
export function sitywebAcceptsUpload(mimeType: string | null, sizeBytes: number | null): boolean {
  const mime = mimeType?.split(";", 1)[0]?.trim().toLowerCase() ?? "";
  const limit = SITYWEB_UPLOAD_LIMITS[mime];
  return limit !== undefined && sizeBytes !== null && sizeBytes > 0 && sizeBytes <= limit;
}

export const sitywebCreateDraftRequestSchema = z
  .object({
    workspaceId: uuidSchema,
    leadId: uuidSchema,
    input: createDraftInputSchema,
    requestedBy: sitywebDraftRequesterSchema.optional(),
    /** Trusted envelope, outside the editable brief. */
    sitywebRequestId: uuidSchema.optional(),
  })
  .strict();
export type SitywebCreateDraftRequest = z.infer<typeof sitywebCreateDraftRequestSchema>;

// T-ONB-07 · Request data only: credentials and file locations never cross the connector.
const requestAttributionValue = z.string().max(200);
export const sitywebRequestSchema = z
  .object({
    id: uuidSchema,
    code: z.string().regex(/^SW-[23456789ABCDEFGHJKLMNPQRSTUVWXYZ]{4}$/),
    feedVersion: z.string().regex(/^[1-9]\d*$/),
    businessName: z.string().min(1).max(120),
    /** Sector as entered in /crea; independent from the canonical create-draft category. */
    category: z.string().min(1).max(80),
    location: z.string().min(1).max(200),
    description: z.string().max(10_000),
    contactName: z.string().min(1).max(120),
    phone: z.string().regex(/^\+[1-9]\d{7,14}$/),
    email: z.string().email().max(320),
    emailVerifiedAt: z.string().datetime().nullable(),
    siteId: uuidSchema.nullable(),
    photos: z
      .object({
        total: z.number().int().min(0).max(8),
        pending: z.number().int().min(0).max(8),
        processing: z.number().int().min(0).max(8),
        ready: z.number().int().min(0).max(8),
        failed: z.number().int().min(0).max(8),
      })
      .strict(),
    attribution: z
      .object({
        utmSource: requestAttributionValue.optional(),
        utmMedium: requestAttributionValue.optional(),
        utmCampaign: requestAttributionValue.optional(),
        utmTerm: requestAttributionValue.optional(),
        utmContent: requestAttributionValue.optional(),
        gclid: z.string().max(512).optional(),
        fbclid: z.string().max(512).optional(),
        landingPage: z
          .string()
          .url()
          .regex(/^https?:\/\//i)
          .max(2048)
          .optional(),
      })
      .strict(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict();
export type SitywebRequest = z.infer<typeof sitywebRequestSchema>;

export const sitywebRequestsQuerySchema = z
  .object({
    workspaceId: uuidSchema,
    after: z.string().min(1).max(1024).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(100),
  })
  .strict();
export const sitywebRequestsResponseSchema = z
  .object({
    requests: z.array(sitywebRequestSchema).max(100),
    cursor: z.string().min(1).max(1024),
    hasMore: z.boolean(),
  })
  .strict();
export type SitywebRequestsResponse = z.infer<typeof sitywebRequestsResponseSchema>;

export const sitywebNotifyReadyRequestSchema = z
  .object({
    workspaceId: uuidSchema,
    leadId: uuidSchema,
    sitywebRequestId: uuidSchema,
    approval: z
      .object({
        actionId: uuidSchema,
        proposalId: uuidSchema,
        proposalVersion: z.number().int().min(1),
        approvedByMemberId: uuidSchema,
        approvedAt: z.string().datetime(),
      })
      .strict(),
  })
  .strict();
export type SitywebNotifyReadyRequest = z.infer<typeof sitywebNotifyReadyRequestSchema>;
export const sitywebNotifyReadyResponseSchema = z
  .object({
    emailId: z.string().min(1).max(120),
    status: z.enum(["pending", "sent", "failed"]),
    alreadyQueued: z.boolean(),
  })
  .strict();
export type SitywebNotifyReadyResponse = z.infer<typeof sitywebNotifyReadyResponseSchema>;
